/**
 * Represents a conflict where a file is attached to a checklist item
 * Used for file guard warnings when deleting/renaming files
 */
export class FileAttachmentConflict {
  public readonly checklistItemName: string;
  public readonly credentialingRequestId: string;
  public readonly credentialingRequestType: string;
  public readonly credentialingRequestName: string;
  public readonly filePath: string;

  private constructor(
    checklistItemName: string,
    credentialingRequestId: string,
    credentialingRequestType: string,
    credentialingRequestName: string,
    filePath: string
  ) {
    this.checklistItemName = checklistItemName;
    this.credentialingRequestId = credentialingRequestId;
    this.credentialingRequestType = credentialingRequestType;
    this.credentialingRequestName = credentialingRequestName;
    this.filePath = filePath;
  }

  /**
   * Get a unique key for grouping conflicts by credentialing request
   */
  private getGroupKey(): string {
    return `${this.credentialingRequestId}_${this.credentialingRequestType}`;
  }

  /**
   * Create a FileAttachmentConflict instance
   */
  public static create(
    checklistItemName: string,
    credentialingRequestId: string,
    credentialingRequestType: string,
    credentialingRequestName: string,
    filePath: string
  ): FileAttachmentConflict {
    return new FileAttachmentConflict(
      checklistItemName,
      credentialingRequestId,
      credentialingRequestType,
      credentialingRequestName,
      filePath
    );
  }

  /**
   * Group conflicts by credentialing request
   * Returns a Map with group keys to arrays of conflicts
   */
  public static groupByCredReq(
    conflicts: FileAttachmentConflict[]
  ): Map<string, FileAttachmentConflict[]> {
    const grouped = new Map<string, FileAttachmentConflict[]>();

    for (const conflict of conflicts) {
      const key = conflict.getGroupKey();

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }

      grouped.get(key)!.push(conflict);
    }

    return grouped;
  }

  /**
   * Get unique checklist item names from an array of conflicts
   * Useful for de-duplicating when multiple files are attached to the same item
   */
  public static getUniqueChecklistItems(conflicts: FileAttachmentConflict[]): string[] {
    const uniqueNames = new Set<string>();
    conflicts.forEach((c) => uniqueNames.add(c.checklistItemName));
    return Array.from(uniqueNames).sort();
  }
}
